import React, { useEffect, useState, useCallback } from 'react'
import { Table, Button, Input, Select, Tag, Space, message, Typography, Badge, Avatar } from 'antd'
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons'
import { productApi } from '../../services/api'
import ProductModal from './ProductModal'

const { Title } = Typography

const CATEGORIES = ['硬件', '软件', '网络设备', '线材辅料', '服务']
const CATEGORY_COLORS = {
  硬件: 'blue',
  软件: 'green',
  网络设备: 'orange',
  线材辅料: 'purple',
  服务: 'cyan',
}

export default function Products() {
  const [data, setData] = useState([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [query, setQuery] = useState({ page: 1, pageSize: 20, keyword: '', category: '' })

  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState(null)

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const res = await productApi.list(query)
      setData(res.data)
      setTotal(res.total)
    } finally {
      setLoading(false)
    }
  }, [query])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const handleDelete = (record) => {
    message.warning('仅管理员可删除产品')
  }

  const getStockStatus = (record) => {
    const qty = record.inventories?.[0]?.quantity || 0
    const minStock = record.minStock || 0
    if (qty === 0) return { status: 'error', text: qty }
    if (qty <= minStock) return { status: 'warning', text: qty }
    return { status: 'success', text: qty }
  }

  const columns = [
    {
      title: '图片', key: 'image', width: 64,
      render: (_, r) => r.imageUrl
        ? <Avatar shape="square" size={40} src={r.imageUrl} alt={r.name} />
        : <Avatar shape="square" size={40} style={{ backgroundColor: '#f0f0f0', color: '#bbb' }}>{r.name?.[0] || '?'}</Avatar>,
    },
    {
      title: 'SKU', dataIndex: 'sku', key: 'sku', width: 100,
    },
    {
      title: '产品名称', dataIndex: 'name', key: 'name',
    },
    {
      title: '分类', dataIndex: 'category', key: 'category', width: 120,
      render: (v) => <Tag color={CATEGORY_COLORS[v] || 'default'}>{v}</Tag>,
    },
    {
      title: '规格', dataIndex: 'spec', key: 'spec', width: 100,
      render: (v) => v || '—',
    },
    {
      title: '目录价', dataIndex: 'listPrice', key: 'listPrice', width: 120,
      render: (v) => v ? `¥${Number(v).toLocaleString()}` : '—',
    },
    {
      title: '库存', key: 'stock', width: 100,
      render: (_, record) => {
        const s = getStockStatus(record)
        return <Badge status={s.status} text={s.text} />
      },
    },
    {
      title: '状态', dataIndex: 'status', key: 'status', width: 80,
      render: (v) => {
        const colorMap = { '在售': 'green', '停售': 'red' }
        return <Tag color={colorMap[v] || 'default'}>{v}</Tag>
      },
    },
    {
      title: '操作', key: 'actions', width: 150,
      render: (_, r) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => {
            setEditing(r)
            setModalOpen(true)
          }}>编辑</Button>
          <Button size="small" danger icon={<DeleteOutlined />} onClick={() => handleDelete(r)}>删除</Button>
        </Space>
      ),
    },
  ]

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>产品管理</Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => { setEditing(null); setModalOpen(true) }}>新增产品</Button>
      </div>

      <Space style={{ marginBottom: 16 }}>
        <Input.Search
          placeholder="搜索产品名称/SKU/供应商"
          allowClear
          style={{ width: 260 }}
          onSearch={(v) => setQuery((q) => ({ ...q, keyword: v, page: 1 }))}
        />
        <Select
          placeholder="筛选分类"
          allowClear
          style={{ width: 140 }}
          onChange={(v) => setQuery((q) => ({ ...q, category: v || '', page: 1 }))}
        >
          {CATEGORIES.map((c) => (
            <Select.Option key={c} value={c}>{c}</Select.Option>
          ))}
        </Select>
      </Space>

      <Table
        columns={columns}
        dataSource={data}
        rowKey="id"
        loading={loading}
        pagination={{
          total,
          current: query.page,
          pageSize: query.pageSize,
          showTotal: (t) => `共 ${t} 个产品`,
          onChange: (page) => setQuery((q) => ({ ...q, page })),
        }}
      />

      <ProductModal
        open={modalOpen}
        editing={editing}
        onClose={() => { setModalOpen(false); setEditing(null) }}
        onSuccess={() => { setModalOpen(false); setEditing(null); fetchData() }}
      />
    </div>
  )
}
